import { useTranslation } from "react-i18next";
import { createStyles } from "antd-style";

import type { PublicJobPostDetailsModel } from "@av/apps/recruitment/ats-common/types";
import { Card } from "@av/components/atoms/Card";
import { List } from "@av/components/atoms/List";
import { Typography } from "@av/components/atoms/typography/Typography";
import { JobListItem } from "./JobListItem";

const { Title } = Typography;

const useStyles = createStyles(({ token, css }) => ({
  listCard: css`
    box-shadow: ${token.boxShadowTertiary};
  `,
  listHeader: css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: ${token.margin}px ${token.marginLG}px;
  `,
  jobList: css`
    border-top: 1px solid ${token.colorSplit};
    & .ant-list-item {
      padding-left: ${token.paddingLG}px;
      padding-right: ${token.paddingLG}px;
    }
    & .ant-list-pagination {
      margin: ${token.marginSM}px;
    }
  `,
  jobTitle: css`
    margin-bottom: ${token.marginXXS}px;
  `,
}));

interface JobListProps {
  visibleJobs: PublicJobPostDetailsModel[];
  allJobs: PublicJobPostDetailsModel[];
  selectedJobId: string | undefined;
  total: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  pageHref: (page: number) => string | undefined;
  testIdPrefix: string;
}

export function JobList({
  visibleJobs,
  allJobs,
  selectedJobId,
  total,
  page,
  pageSize,
  onPageChange,
  pageHref,
  testIdPrefix,
}: JobListProps) {
  const { styles } = useStyles();
  const { t } = useTranslation();
  const selectedJob: PublicJobPostDetailsModel | undefined = allJobs.find(
    (j) => j.jobPostId === selectedJobId
  );
  let localVisibleJobs = visibleJobs;

  if (
    selectedJob &&
    (selectedJob.postStatus === "CLOSED" || selectedJob.postStatus === "ARCHIVED") &&
    !visibleJobs.some((j) => j.jobPostId === selectedJob.jobPostId)
  ) {
    localVisibleJobs = [selectedJob, ...visibleJobs];
  }

  return (
    <Card testIdPrefix="job-list" className={styles.listCard} styles={{ body: { padding: 0 } }}>
      <div className={styles.listHeader}>
        <Title level={5} className={styles.jobTitle} testIdPrefix={`${testIdPrefix}-list`}>
          {t("recruitment.jobs.JobsTitle", { total: total })}
        </Title>
      </div>

      {localVisibleJobs.length > 0 && (
        <>
          <List
            itemLayout="horizontal"
            className={styles.jobList}
            dataSource={localVisibleJobs}
            renderItem={(job) => (
              <JobListItem
                key={job.jobPostId}
                job={job}
                testIdPrefix={testIdPrefix}
                isSelectedJob={job.jobPostId === selectedJobId}
              />
            )}
            testIdPrefix={`${testIdPrefix}-jobs`}
            pagination={{
              size: "small",
              current: page,
              pageSize: pageSize,
              total: total,
              onChange: onPageChange,
              pageHref,
              showSizeChanger: false,
            }}
          />
        </>
      )}
    </Card>
  );
}
